import { z } from 'zod';
import { AUDIENCE_FOR_ROLE, PLATFORM_ROLES, } from '../shared/audience';
/** `GET /api/auth/session` — who am I, right now. */
/** Who is viewing whom, and until when. */
export const ImpersonationView = z.object({
    sessionId: z.string().uuid(),
    /** The administrator, by name, as the bar shows it. */
    adminName: z.string(),
    expiresAt: z.string(),
});
export const SessionResponse = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    fullName: z.string(),
    /**
     * What to greet them by — **not** `fullName.split(' ')[0]`.
     *
     * Non-nullable by contract: the server answers `users.given_name` when
     * somebody has corrected it and a derivation when nobody has, so a client
     * never decides what to print and a one-word name answers itself.
     *
     * The derivation is the same honorific rule we ran client-side for a day,
     * moved to where it belongs — the backend's reasoning being the one we gave
     * them: `Me` is an honorific in Québec and a pronoun in English, which is
     * domain knowledge, and four codebases each keeping that list is four lists
     * that drift.
     */
    givenName: z.string(),
    platformRole: z.enum(PLATFORM_ROLES),
    /** The back office's concern. A member holds none; nothing here reads it. */
    adminRoles: z.array(z.string()),
    capabilities: z.array(z.string()),
    /**
     * The language this platform WRITES to them in — mail, not chrome.
     *
     * ── `null` is a state, and defaulting it away destroys the field ──────────
     * Nullable on purpose and passed straight through. "Never chose a language"
     * and "chose one" want opposite handling from a language toggle: the first
     * may quietly follow whatever interface they are in, the second must never be
     * overwritten by whatever browser they happen to sign in on. Somebody using a
     * hotel computer with an English browser should not have their account's mail
     * language silently rewritten.
     *
     * Collapsing null to `'fr'` here would make every account look like it had
     * chosen, which is the one thing this field exists to be able to deny.
     *
     * `.nullish()` rather than `.nullable()` for the usual rollout reason: a
     * server that predates the field omits it, and a required key would take the
     * whole session read down.
     */
    locale: z.string().nullish(),
    /** Newsletter consent. Optional for the rollout reason `locale` gives. */
    marketingConsent: z.boolean().optional(),
    /**
     * Short-lived signed URL for the member's own portrait — `null` when they
     * have not set one, when the derivative has not built yet, or when this API
     * still predates the field.
     *
     * `.nullish()` for the same rollout reason `locale` is: a session written
     * before the field existed omits it, and a required key would take the
     * whole session read down.
     */
    photoUrl: z.string().nullish(),
    /**
     * The family photo — the picture of the crew — which is **not** the portrait
     * (11 Oct 2026). Nullish for the rollout reason `photoUrl` is.
     */
    familyPhotoUrl: z.string().nullish(),
    /**
     * Set when an administrator is viewing the platform as this member
     * (« Log in as this member », 16 Oct 2026) — the shell draws a bar across the
     * top saying so, with « Return to admin ». Nullish for the rollout reason
     * `locale` gives: an API that predates it sends nothing, which is a member's
     * own session.
     */
    impersonation: ImpersonationView.nullish(),
});
/**
 * `GET /api/entitlement` — the single correct answer, built for us 2026-08-01.
 *
 * This REPLACES the tri-state we shipped as a mitigation. That existed because
 * `GET /api/subscriptions/mine` filters on `userId` and a business subscription
 * is stored against the organisation, so it answered `{"items":[]}` for every
 * seat holder — and collapsing an empty list to `false` would have shown a
 * paywall to a paying organisation's staff.
 *
 * The backend fixed the answer rather than moving it, which is what we asked
 * for once we understood the problem. Driven by them with a real seat holder:
 *
 *   GET /api/subscriptions/mine  →  {"items":[]}
 *   GET /api/entitlement         →  {"entitled":true,"source":"ORGANISATION",…}
 *
 * `source: 'NONE'` is a real answer rather than an absence, which is precisely
 * what lets the tri-state collapse: we can now tell "no" from "we could not
 * look", and only the second one withholds a lock.
 */
export const BillingPeriod = z.enum(['MONTH', 'QUARTER', 'YEAR']);
export const EntitlementSource = z.enum(['SUBSCRIPTION', 'ORGANISATION', 'NONE']);
export const EntitlementResponse = z.object({
    entitled: z.boolean(),
    source: EntitlementSource,
    planKey: z.string().nullable(),
    planName: z.string().nullable(),
    /**
     * The organisation providing access, when `source` is `ORGANISATION`.
     *
     * A seat holder cannot fetch this themselves — organisation reads are scoped
     * to an administrator. Without it the billing screen can say "your employer"
     * and not which one, which is a worse answer than none on a screen whose job
     * is explaining why there is no cancel button.
     */
    organisationName: z.string().nullable(),
    /**
     * `MONTH | QUARTER | YEAR` — the word the design's licence panel and rail
     * pill actually want (`Pro · Annual`), mirroring `planVersion.period`.
     *
     * **Null for `source: 'ORGANISATION'`, and that is a statement.** A seat
     * holder has no billing period of their own; their employer has one, and
     * printing the organisation's beside *their* name would describe a renewal
     * they neither pay for nor control. Null for `NONE` for the obvious reason.
     */
    period: BillingPeriod.nullable(),
    currentPeriodEnd: z.string().datetime().nullable(),
});
export function entitlementFrom(response) {
    if (!response)
        return 'unknown';
    return response.entitled ? 'entitled' : 'not-entitled';
}
export function viewerFrom(session, entitlement) {
    return {
        session,
        audience: AUDIENCE_FOR_ROLE[session.platformRole],
        entitlement: entitlementFrom(entitlement),
        access: entitlement,
    };
}
/**
 * Whether to offer a "manage your subscription" path.
 *
 * A member entitled through their employer's seat has no authority over it, so
 * sending them to a cancel flow leads nowhere they can act. This is the reason
 * `source` exists.
 */
export function canManageOwnSubscription(access) {
    return access?.source === 'SUBSCRIPTION';
}
/** Only a definite negative locks. `unknown` shows the content and lets the server decide. */
export function shouldLock(entitlement, premium) {
    return premium && entitlement === 'not-entitled';
}
/**
 * Surfaces that exist only for some roles. The nav registry reads this.
 *
 * `staff` (the session holds an admin role) adds `STAFF_SURFACES`: the team
 * reads every audience, so a staff member on a Pro account sees the parents'
 * weekly challenge too (Krysta, 7 Oct 2026).
 */
export function canSee(role, surface, staff = false) {
    return staff || SURFACES[surface].includes(role);
}
/*
 * A team member sees EVERY surface, whatever their platform role (operator,
 * 8 Oct 2026: « everyone who has back-office access … access to everything »).
 * The API already lets the team read every audience; this opens every screen —
 * both portals' sections, the cohort journey, the crew, certificates and seats.
 * A screen about something the person does not have (no cohort, no
 * organisation) shows its own empty state rather than being hidden.
 */
/** Whether this session is a team member's — any admin role at all. */
export function isStaff(session) {
    return (session?.adminRoles.length ?? 0) > 0;
}
/**
 * Full `Record`, so adding a surface fails to compile until someone decides who
 * sees it — the same discipline the backend's mediators use.
 *
 * From the design: Pro removes Q&A, Weekly Challenge and anything child-related
 * and adds Case Studies; TSP is Pro plus the cohort. `seats` is ORG_ADMIN's,
 * and the backend has now built the four `/organisations/mine/…` routes behind
 * it.
 *
 * ── `certificates` was TSP-only here, and that was a misreading ───────────
 * It came from where the STRIP sits: `CertStrip` is defined in `pro-app.jsx`
 * but only `TspApp`'s home renders it (`tsp-app.jsx:401`), so the row on Home
 * is genuinely TSP's. The certificates themselves are not — they are earned
 * off the Pro library, described on Pro Resources, and the backend confirmed
 * on 2026-08-19 that issuance requires only a practitioner and a PRO/TSP
 * course. A Pro member holds them; they just have no strip on Home about it.
 *
 * So `certificates` now records who HAS them and `certificateStrip` who gets
 * the summary row on Home — two questions one entry was answering with one
 * answer, which is what would have made a practitioner's own credentials
 * invisible to them.
 *
 * ── ORG_ADMIN is a Pro account, confirmed 2026-08-20 ──────────────────────
 * > an org admin is a pro account with extra privileges to add "seats" or
 * > users to their account
 *
 * So they join `caseStudies` and `certificates`. This is not a widening for
 * tidiness — it fixes a **dead nav item**: `NAV_BY_ROLE.ORG_ADMIN` is
 * `PRO_NAV`, which offers Case Studies, and `requireSurface('caseStudies')`
 * then redirected them to Home. Two tables disagreeing about one role, in the
 * file that calls itself the only place role branches.
 *
 * `cohort` stays TSP's — extra privileges over a Pro account are seats, not a
 * cohort enrolment — and `certificateStrip` stays TSP's for the same reason it
 * always was: it is about the Home row, not about holding certificates.
 *
 * These reads answer **0** for an org admin today, because the backend maps
 * `ORG_ADMIN → ORG` and nothing carries the ORG audience. That is request 15
 * §1 and it is the right shape of problem to have: empty because of data,
 * rather than unreachable because of a guard we wrote.
 */
const SURFACES = {
    /*
     * The voyage — the PRO/parent « My Journey ». Operator rule (21 Aug 2026):
     * a TSP member holds ONE journey, the cohort's; the voyage arrives with the
     * Pro role when their cohort closes. Two journeys side by side read as two
     * competing progress systems for a member whose programme is the cohort.
     */
    journey: ['PARENT', 'PRO', 'ORG_ADMIN'],
    qanda: ['PARENT'],
    challenge: ['PARENT'],
    crew: ['PARENT'],
    caseStudies: ['PRO', 'TSP', 'ORG_ADMIN'],
    cohort: ['TSP'],
    certificates: ['PRO', 'TSP', 'ORG_ADMIN'],
    certificateStrip: ['TSP'],
    seats: ['ORG_ADMIN'],
};
//# sourceMappingURL=session.js.map